import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    App as AntdApp,
    Button,
    Form,
    Input,
    Modal,
    Select,
    Table,
    Typography,
} from "antd";
import {
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
    FilterActions,
    FilterBar,
    FilterField,
} from "../../components/common/FilterBar";
import {
    buildPaginatedListPagination,
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
} from "../../components/common/PaginatedListTable";
import PageLayout from "../../components/layout/PageLayout/PageLayout";
import { usePermissions } from "../../hooks/usePermissions";
import {
    createValidationRule,
    getValidationRule,
    listValidationRules,
    setValidationRuleVersionDisabled,
    updateValidationRule,
} from "../../services/modelValidationService";
import type {
    ValidationRule,
    ValidationRuleConfig,
    ValidationRuleVersion,
} from "../../types/modelValidation";
import { MODEL_ACTIVATE_ACCESS_PERMISSIONS } from "../../utils/permissions";
import { getModelManagementErrorDetail } from "./modelManagement/pageConfig";
import {
    getCurrentValidationVersion,
    isValidationRuleConfigValid,
} from "./modelValidation/modelValidationUtils";
import ValidationRuleEditor from "./modelValidation/ValidationRuleEditor";
import { buildValidationRuleColumns } from "./modelValidation/validationRuleColumns";
import "./ModelValidationRulesPage.css";

const PAGE_SIZE = 10;

const emptyConfig = (): ValidationRuleConfig => ({
    category_weights: {
        aedes: 6000,
        culex: 2500,
        otherMosquito: 1500,
    },
    metric_weights: {
        recognition: 5000,
        error_control: 2500,
        miss_control: 2500,
    },
    standards: {
        min_composite_score_bp: 7000,
        min_composite_improvement_bp: 1000,
        max_weighted_error_rate_bp: 3000,
        max_weighted_miss_rate_bp: 1000,
    },
});

interface RuleFormValues {
    description?: string;
    name: string;
}

interface ModelValidationRulesPageProps {
    embedded?: boolean;
}

const ModelValidationRulesPage: React.FC<ModelValidationRulesPageProps> = ({
    embedded = false,
}) => {
    const { message, modal } = AntdApp.useApp();
    const { t } = useTranslation(["admin", "navigation", "common"]);
    const { hasAnyPermissions } = usePermissions();
    const canActivate = hasAnyPermissions(MODEL_ACTIVATE_ACCESS_PERMISSIONS);
    const [createForm] = Form.useForm<RuleFormValues>();
    const [editForm] = Form.useForm<RuleFormValues>();
    const [items, setItems] = useState<ValidationRule[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [keywordDraft, setKeywordDraft] = useState("");
    const [statusFilterDraft, setStatusFilterDraft] = useState<string>();
    const [keyword, setKeyword] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<ValidationRule | null>(
        null,
    );
    const [editConfig, setEditConfig] =
        useState<ValidationRuleConfig>(emptyConfig());
    const [createConfig, setCreateConfig] =
        useState<ValidationRuleConfig>(emptyConfig());

    const loadRules = useCallback(async () => {
        setLoading(true);
        try {
            const response = await listValidationRules({
                keyword: keyword || undefined,
                status: statusFilter,
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setItems(response.items);
            setTotal(response.total);
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.loadRulesFailed"),
                    t,
                ),
            );
        } finally {
            setLoading(false);
        }
    }, [keyword, message, page, pageSize, statusFilter, t]);

    useEffect(() => {
        void loadRules();
    }, [loadRules]);

    const applyFilters = () => {
        setPage(1);
        setKeyword(keywordDraft.trim());
        setStatusFilter(statusFilterDraft);
    };

    const resetFilters = () => {
        setKeywordDraft("");
        setStatusFilterDraft(undefined);
        setPage(1);
        setKeyword("");
        setStatusFilter(undefined);
    };

    const selectedVersion = useMemo<ValidationRuleVersion | null>(() => {
        if (!selectedRule) return null;
        return (
            getCurrentValidationVersion(
                selectedRule.versions,
                selectedRule.latest_published_version,
            ) || null
        );
    }, [selectedRule]);

    const updateSelectedRule = useCallback(
        (rule: ValidationRule) => {
            const version = getCurrentValidationVersion(
                rule.versions,
                rule.latest_published_version,
            );
            setSelectedRule(rule);
            editForm.setFieldsValue({
                name: rule.name,
                description: rule.description || undefined,
            });
            setEditConfig(version?.config || emptyConfig());
            void loadRules();
        },
        [editForm, loadRules],
    );

    const openRule = async (rule: ValidationRule) => {
        setLoading(true);
        try {
            updateSelectedRule(await getValidationRule(rule.stable_id));
            setDetailOpen(true);
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.loadRuleFailed"),
                    t,
                ),
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        const values = await createForm.validateFields();
        setSaving(true);
        try {
            await createValidationRule({
                ...values,
                config: createConfig,
            });
            setCreateOpen(false);
            createForm.resetFields();
            setCreateConfig(emptyConfig());
            message.success(t("admin:modelValidation.messages.ruleCreated"));
            void loadRules();
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.createRuleFailed"),
                    t,
                ),
            );
        } finally {
            setSaving(false);
        }
    };

    const handleRuleAction = async (
        action: () => Promise<ValidationRule>,
        successMessageKey?: string,
    ): Promise<ValidationRule | null> => {
        setSaving(true);
        try {
            const rule = await action();
            updateSelectedRule(rule);
            if (successMessageKey) message.success(t(successMessageKey));
            return rule;
        } catch (error) {
            message.error(
                getModelManagementErrorDetail(
                    error,
                    t("admin:modelValidation.messages.operationFailed"),
                    t,
                ),
            );
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (!selectedRule || !selectedVersion || !canActivate) {
            return;
        }
        const values = await editForm.validateFields();
        await handleRuleAction(
            () =>
                updateValidationRule(selectedRule.stable_id, {
                    ...values,
                    lockVersion: selectedVersion.lock_version,
                    config: editConfig,
                }),
            "admin:modelValidation.messages.ruleSaved",
        );
    };

    const handleRuleStatusChange = (
        rule: ValidationRule,
        disabled: boolean,
    ) => {
        const version = getCurrentValidationVersion(
            rule.versions,
            rule.latest_published_version,
        );
        if (!version) return;
        modal.confirm({
            cancelText: t("admin:modelValidation.actions.cancel"),
            content: t(
                `admin:modelValidation.confirmations.${disabled ? "disableRuleDescription" : "enableRuleDescription"}`,
            ),
            okButtonProps: { danger: disabled },
            okText: t(
                `admin:modelValidation.actions.${disabled ? "disable" : "restore"}`,
            ),
            maskClosable: false,
            onOk: () =>
                handleRuleAction(
                    () =>
                        setValidationRuleVersionDisabled(
                            rule.stable_id,
                            version.version_no,
                            version.lock_version,
                            disabled,
                        ),
                    `admin:modelValidation.messages.${disabled ? "ruleDisabled" : "ruleEnabled"}`,
                ),
            title: t(
                `admin:modelValidation.confirmations.${disabled ? "disableRuleTitle" : "enableRuleTitle"}`,
            ),
        });
    };

    const columns = buildValidationRuleColumns({
        t,
        canActivate,
        onOpen: (rule) => void openRule(rule),
        onSetDisabled: handleRuleStatusChange,
    });

    return (
        <PageLayout
            embedded={embedded}
            title={t("admin:modelValidation.rules.title")}
            breadcrumb={[
                { title: t("navigation:breadcrumb.modelManagement") },
                { title: t("admin:modelValidation.rules.title") },
            ]}
        >
            <FilterBar>
                <FilterField label={t("admin:modelValidation.filters.name")}>
                    <Input
                        allowClear
                        onChange={(event) =>
                            setKeywordDraft(event.target.value)
                        }
                        onPressEnter={applyFilters}
                        placeholder={t(
                            "admin:modelValidation.rules.searchPlaceholder",
                        )}
                        value={keywordDraft}
                    />
                </FilterField>
                <FilterField label={t("admin:modelValidation.filters.status")}>
                    <Select
                        allowClear
                        onChange={setStatusFilterDraft}
                        options={["published", "disabled"].map((value) => ({
                            value,
                            label: t(`admin:modelValidation.status.${value}`),
                        }))}
                        value={statusFilterDraft}
                    />
                </FilterField>
                <FilterActions align="left">
                    <Button
                        aria-label={t("common:buttons.search")}
                        icon={<SearchOutlined />}
                        onClick={applyFilters}
                        type="primary"
                    >
                        {t("common:buttons.search")}
                    </Button>
                    <Button
                        aria-label={t("common:buttons.reset")}
                        icon={<ReloadOutlined />}
                        onClick={resetFilters}
                    >
                        {t("common:buttons.reset")}
                    </Button>
                </FilterActions>
            </FilterBar>
            <PaginatedListContainer>
                <PaginatedListToolbar>
                    {canActivate ? (
                        <Button
                            aria-label={t("common:buttons.add")}
                            icon={<PlusOutlined />}
                            onClick={() => setCreateOpen(true)}
                            type="primary"
                        >
                            {t("common:buttons.add")}
                        </Button>
                    ) : null}
                </PaginatedListToolbar>
                <PaginatedListCard>
                    <Table
                        columns={columns}
                        dataSource={items}
                        loading={loading}
                        pagination={buildPaginatedListPagination({
                            current: page,
                            pageSize,
                            total,
                            showTotal: (count) =>
                                t("admin:modelValidation.pagination.totalRecords", {
                                    total: count,
                                }),
                            onChange: (nextPage, nextPageSize) => {
                                const resolvedPageSize = nextPageSize ?? pageSize;
                                setPage(
                                    resolvedPageSize !== pageSize ? 1 : nextPage,
                                );
                                setPageSize(resolvedPageSize);
                            },
                        })}
                        rowKey="stable_id"
                        scroll={{ x: 860 }}
                        size={PAGINATED_LIST_TABLE_SIZE}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>

            <Modal
                cancelButtonProps={{ "aria-label": t("common:buttons.cancel") }}
                cancelText={t("common:buttons.cancel")}
                confirmLoading={saving}
                destroyOnClose
                maskClosable={false}
                onCancel={() => setCreateOpen(false)}
                onOk={() => void handleCreate()}
                okButtonProps={{
                    "aria-label": t("common:buttons.save"),
                    disabled: !isValidationRuleConfigValid(createConfig),
                }}
                okText={t("common:buttons.save")}
                open={createOpen}
                styles={{ body: { maxHeight: "68vh", overflowY: "auto" } }}
                title={t("admin:modelValidation.actions.createRule")}
                width="min(1120px, 96vw)"
            >
                <div className="validation-rule-dialog-content">
                    <section
                        aria-label={t(
                            "admin:modelValidation.rules.basicInformation",
                        )}
                        className="validation-rule-section validation-rule-basic-section"
                    >
                        <div className="validation-rule-section-header">
                            <Typography.Title level={5} style={{ margin: 0 }}>
                                {t(
                                    "admin:modelValidation.rules.basicInformation",
                                )}
                            </Typography.Title>
                        </div>
                        <div className="validation-rule-section-body">
                            <Form
                                className="validation-rule-basic-form"
                                form={createForm}
                                layout="vertical"
                            >
                                <Form.Item
                                    label={t(
                                        "admin:modelValidation.fields.name",
                                    )}
                                    name="name"
                                    rules={[{ required: true, max: 128 }]}
                                >
                                    <Input maxLength={128} />
                                </Form.Item>
                                <Form.Item
                                    label={t(
                                        "admin:modelValidation.fields.description",
                                    )}
                                    name="description"
                                    rules={[{ max: 1000 }]}
                                >
                                    <Input.TextArea
                                        autoSize={{ minRows: 3, maxRows: 6 }}
                                        maxLength={1000}
                                        showCount
                                    />
                                </Form.Item>
                            </Form>
                        </div>
                    </section>
                    <ValidationRuleEditor
                        onChange={setCreateConfig}
                        value={createConfig}
                    />
                </div>
            </Modal>

            <Modal
                cancelButtonProps={{ "aria-label": t("common:buttons.cancel") }}
                cancelText={t("common:buttons.cancel")}
                centered
                confirmLoading={saving}
                destroyOnClose
                footer={
                    selectedRule && !canActivate ? (
                        <Button onClick={() => setDetailOpen(false)}>
                            {t("admin:modelValidation.actions.close")}
                        </Button>
                    ) : undefined
                }
                onCancel={() => setDetailOpen(false)}
                maskClosable={false}
                onOk={() => void handleSave()}
                okButtonProps={{
                    "aria-label": t("common:buttons.save"),
                    disabled: !isValidationRuleConfigValid(editConfig),
                }}
                okText={t("common:buttons.save")}
                open={detailOpen}
                styles={{
                    body: {
                        maxHeight: "min(68vh, calc(100dvh - 176px))",
                        overflowX: "hidden",
                        overflowY: "auto",
                    },
                }}
                title={t(
                    `admin:modelValidation.rules.${canActivate ? "editTitle" : "viewTitle"}`,
                )}
                width="min(1120px, calc(100vw - 24px))"
            >
                {selectedRule && selectedVersion ? (
                    <div className="validation-rule-dialog-content">
                        <section
                            aria-label={t(
                                "admin:modelValidation.rules.basicInformation",
                            )}
                            className="validation-rule-section validation-rule-basic-section"
                        >
                            <div className="validation-rule-section-header">
                                <Typography.Title
                                    level={5}
                                    style={{ margin: 0 }}
                                >
                                    {t(
                                        "admin:modelValidation.rules.basicInformation",
                                    )}
                                </Typography.Title>
                            </div>
                            <div className="validation-rule-section-body">
                                <Form
                                    className="validation-rule-basic-form"
                                    disabled={!canActivate}
                                    form={editForm}
                                    layout="vertical"
                                >
                                    <Form.Item
                                        label={t(
                                            "admin:modelValidation.fields.name",
                                        )}
                                        name="name"
                                        rules={[{ required: true, max: 128 }]}
                                    >
                                        <Input maxLength={128} />
                                    </Form.Item>
                                    <Form.Item
                                        label={t(
                                            "admin:modelValidation.fields.description",
                                        )}
                                        name="description"
                                        rules={[{ max: 1000 }]}
                                    >
                                        <Input.TextArea
                                            autoSize={{
                                                minRows: 3,
                                                maxRows: 6,
                                            }}
                                            maxLength={1000}
                                            showCount
                                        />
                                    </Form.Item>
                                </Form>
                            </div>
                        </section>

                        <ValidationRuleEditor
                            disabled={!canActivate}
                            onChange={setEditConfig}
                            value={editConfig}
                        />
                    </div>
                ) : null}
            </Modal>
        </PageLayout>
    );
};

export default ModelValidationRulesPage;
